import React, { useRef, useEffect, useState } from 'react';
import Tooltip from '../../Tooltip/Tooltip';
import { STATUS_LABELS } from './constants';
import { Icon } from '@iq/react-components';

/**
 * Renders a point group value with middle truncation via CSS flexbox.
 * The string is split at its midpoint: the first half shrinks with a CSS
 * ellipsis while the second half (the important end) always stays visible.
 * A tooltip with the full value is shown only when the prefix is overflowing.
 */
export const PointGroupCell = ({ cell }) => {
  const fullText = cell.value ?? '';
  const containerRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const splitIndex = Math.ceil(fullText.length / 2);
  const prefix = fullText.slice(0, splitIndex);
  const suffix = fullText.slice(splitIndex);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const check = () => {
      const prefixSpan = element.firstElementChild;
      const suffixSpan = element.lastElementChild;
      const prefixTruncated = !!prefixSpan && prefixSpan.scrollWidth > prefixSpan.clientWidth;
      const suffixTruncated =
        !!suffixSpan &&
        suffixSpan !== prefixSpan &&
        suffixSpan.scrollWidth > suffixSpan.clientWidth;
      setIsTruncated(prefixTruncated || suffixTruncated);
    };

    const observer = new ResizeObserver(check);
    observer.observe(element);
    check();
    return () => observer.disconnect();
  }, [fullText]);

  return (
    <Tooltip
      content={fullText}
      disabled={!isTruncated}
    >
      <div
        ref={containerRef}
        className="point-group-cell"
      >
        <span className="point-group-prefix">{prefix}</span>
        <span className="point-group-suffix">{suffix}</span>
      </div>
    </Tooltip>
  );
};

export const StatusCell = ({ cell, column }) => {
  const labels = column.statusLabels || STATUS_LABELS;
  const label = labels[cell.value] || 'Unknown';
  const colonIndex = label.indexOf(':');
  const tooltipContent =
    colonIndex !== -1 ? (
      <span>
        {label.slice(0, colonIndex + 1)} <strong>{label.slice(colonIndex + 2)}</strong>
      </span>
    ) : (
      <strong>{label}</strong>
    );
  const IconComponent = column.statusIcons?.[cell.value];
  return (
    <div className="analytics-status-icon">
      <Tooltip
        content={tooltipContent}
        placement="right"
      >
        {IconComponent ? (
          <span className="switchgear-status-icon">
            <IconComponent />
          </span>
        ) : cell.value === 'ok' ? (
          <Icon icon="he-statushealthy" />
        ) : (
          <Icon
            className="he-statuswarning-icon"
            icon="he-statuswarning"
          />
        )}
      </Tooltip>
    </div>
  );
};

/**
 * Generic bar + value cell.
 * Shows a horizontal bar whose width is proportional to the cell value
 * relative to the max value in that column across all visible rows.
 * The max value is derived from `column.maxValue` which should be set
 * by the column definition or computed from data.
 */
export const BarCell = ({ cell, column }) => {
  const value = cell.value;
  const max = column.maxValue || 1;
  const percent = Math.min((value / max) * 100, 100);

  return (
    <div className="bar-cell">
      <div
        className="bar-cell-fill"
        style={{ width: `${percent}%` }}
      />
      <span className="bar-cell-value">{value ?? 0}</span>
    </div>
  );
};
